import { Component } from '@theme/component';
import { morphSection } from '@theme/section-renderer';

class ProductRecommendations extends Component {
  /**
   * The observer for the product recommendations
   * @type {IntersectionObserver}
   */
  #intersectionObserver = new IntersectionObserver(
    (entries, observer) => {
      if (!entries[0]?.isIntersecting) return;

      observer.disconnect();
      this.#loadRecommendations();
    },
    { rootMargin: '0px 0px 400px 0px' }
  );

  /**
   * Observing changes to the elements attributes
   * @type {MutationObserver}
   */
  #mutationObserver = new MutationObserver((mutations) => {
    for (const mutation of mutations) {
      // Only attribute changes are interesting
      if (mutation.target !== this || mutation.type !== 'attributes') continue;

      // Ignore error attribute changes
      if (mutation.attributeName === 'data-error') continue;

      // Ignore addition of hidden class because it means there's an error with the display
      if (mutation.attributeName === 'class' && this.classList.contains('hidden')) continue;

      // Ignore when the data-recommendations-performed attribute has been set to 'true'
      if (
        mutation.attributeName === 'data-recommendations-performed' &&
        this.dataset.recommendationsPerformed === 'true'
      )
        continue;

      // All other attribute changes trigger a reload
      this.#loadRecommendations();
      break;
    }
  });

  /**
   * The cached recommendations
   * @type {Record<string, string>}
   */
  #cachedRecommendations = {};

  /**
   * An abort controller for the active fetch (if there is one)
   * @type {AbortController | null}
   */
  #activeFetch = null;

  /**
   * Event controller for the drawer recommendation carousel.
   * @type {AbortController | null}
   */
  #carouselController = null;

  connectedCallback() {
    super.connectedCallback();
    this.#intersectionObserver.observe(this);
    this.#mutationObserver.observe(this, { attributes: true });
    this.#initializeDrawerCarousel();
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#intersectionObserver.disconnect();
    this.#mutationObserver.disconnect();
    this.#carouselController?.abort();
  }

  /**
   * Load the product recommendations
   */
  #loadRecommendations() {
    const { productId, recommendationsPerformed, sectionId, intent } = this.dataset;

    if (!productId || !sectionId) {
      throw new Error('Product ID and a section ID are required');
    }

    // If the recommendations have already been loaded, accounts for the case where the Theme Editor
    // is loaded the section from the editor's visual preview context.
    if (recommendationsPerformed === 'true') {
      return;
    }

    this.#fetchCachedRecommendations(productId, sectionId, intent)
      .then((result) => {
        if (!result.success) {
          // The Theme Editor will place a section element element in the DOM whose section_id is not available
          // to the Section Renderer API. In this case, we can safely ignore the error.
          if (!Shopify.designMode) {
            this.#handleError(new Error(`Server returned ${result.status}`));
          }
          return;
        }

        if (result.data?.trim().length) {
          this.dataset.recommendationsPerformed = 'true';
          if (this.classList.contains('cart-recommendations')) {
            this.#hydrateCartRecommendations(result.data);
          } else {
            morphSection(sectionId, result.data, { mode: 'hydration', injectStylesheet: true });
          }
        } else {
          this.#handleError(new Error('No recommendations available'));
        }
      })
      .catch((e) => {
        this.#handleError(e);
      });
  }

  /**
   * Hydrates the cart recommendation node directly. The cart drawer's parent
   * intentionally opts out of section morphing so its open dialog, focus and
   * scroll state survive AJAX cart updates.
   *
   * @param {string} html
   */
  #hydrateCartRecommendations(html) {
    const hydrationKey = this.dataset.hydrationKey;
    if (!hydrationKey) return;

    const documentFragment = new DOMParser().parseFromString(html, 'text/html');
    const nextRecommendations = Array.from(
      documentFragment.querySelectorAll('product-recommendations[data-hydration-key]')
    ).find((element) => element.dataset.hydrationKey === hydrationKey);

    if (!nextRecommendations) {
      this.#handleError(new Error('Cart recommendations missing from section response'));
      return;
    }

    this.replaceChildren(...Array.from(nextRecommendations.childNodes).map((node) => document.importNode(node, true)));
    this.dataset.recommendationsPerformed = 'true';
    this.#initializeDrawerCarousel();
  }

  /**
   * Connects the drawer recommendation track to its dot controls.
   */
  #initializeDrawerCarousel() {
    this.#carouselController?.abort();
    this.#carouselController = null;

    if (!this.classList.contains('cart-recommendations--drawer')) return;

    const track = this.querySelector('.cart-recommendations__list');
    const cards = Array.from(this.querySelectorAll('.cart-recommendations__card'));
    const dots = Array.from(this.querySelectorAll('.cart-recommendations__dot'));

    if (!(track instanceof HTMLElement) || cards.length < 2 || dots.length < 2) return;

    const controller = new AbortController();
    this.#carouselController = controller;

    const updateDots = () => {
      const firstCard = cards[0];
      const secondCard = cards[1];
      if (!(firstCard instanceof HTMLElement) || !(secondCard instanceof HTMLElement)) return;

      const step = secondCard.offsetLeft - firstCard.offsetLeft;
      const activeIndex =
        step > 0 ? Math.min(dots.length - 1, Math.max(0, Math.round(track.scrollLeft / step))) : 0;

      dots.forEach((dot, index) => dot.setAttribute('aria-current', index === activeIndex ? 'true' : 'false'));
    };

    track.addEventListener('scroll', updateDots, { passive: true, signal: controller.signal });
    window.addEventListener('resize', updateDots, { passive: true, signal: controller.signal });

    let activePointerId = null;
    let dragStartX = 0;
    let dragStartScroll = 0;
    let dragged = false;
    let suppressClickUntil = 0;

    const finishDrag = (event) => {
      if (!(event instanceof PointerEvent) || event.pointerId !== activePointerId) return;

      if (track.hasPointerCapture(event.pointerId)) {
        track.releasePointerCapture(event.pointerId);
      }

      track.classList.remove('is-dragging');
      activePointerId = null;

      if (!dragged) return;

      suppressClickUntil = Date.now() + 250;
      const firstCard = cards[0];
      const secondCard = cards[1];
      if (!(firstCard instanceof HTMLElement) || !(secondCard instanceof HTMLElement)) return;

      const step = secondCard.offsetLeft - firstCard.offsetLeft;
      if (step <= 0) return;

      const targetIndex = Math.min(cards.length - 1, Math.max(0, Math.round(track.scrollLeft / step)));
      track.scrollTo({
        left: targetIndex * step,
        behavior: window.matchMedia('(prefers-reduced-motion: reduce)').matches ? 'auto' : 'smooth',
      });
    };

    track.addEventListener(
      'pointerdown',
      (event) => {
        if (!(event instanceof PointerEvent) || event.pointerType !== 'mouse' || event.button !== 0) return;

        activePointerId = event.pointerId;
        dragStartX = event.clientX;
        dragStartScroll = track.scrollLeft;
        dragged = false;
      },
      { signal: controller.signal }
    );

    track.addEventListener(
      'pointermove',
      (event) => {
        if (!(event instanceof PointerEvent) || event.pointerId !== activePointerId) return;

        const distance = event.clientX - dragStartX;
        if (!dragged && Math.abs(distance) < 5) return;

        // Preserve link clicks until movement confirms a drag.
        if (!dragged) track.setPointerCapture(event.pointerId);
        dragged = true;
        track.classList.add('is-dragging');
        track.scrollLeft = dragStartScroll - distance;
        event.preventDefault();
      },
      { signal: controller.signal }
    );

    window.addEventListener('pointerup', finishDrag, { signal: controller.signal });
    window.addEventListener('pointercancel', finishDrag, { signal: controller.signal });
    track.addEventListener('dragstart', (event) => event.preventDefault(), { signal: controller.signal });
    track.addEventListener(
      'click',
      (event) => {
        if (Date.now() >= suppressClickUntil) return;

        event.preventDefault();
        event.stopImmediatePropagation();
      },
      { capture: true, signal: controller.signal }
    );

    dots.forEach((dot, index) => {
      dot.addEventListener(
        'click',
        () => {
          const card = cards[index];
          if (!(card instanceof HTMLElement)) return;

          const trackRect = track.getBoundingClientRect();
          const cardRect = card.getBoundingClientRect();
          const maximumScroll = track.scrollWidth - track.clientWidth;
          const targetScroll = Math.min(maximumScroll, track.scrollLeft + cardRect.left - trackRect.left);

          track.scrollTo({
            left: targetScroll,
            behavior: window.matchMedia('(prefers-reduced-motion: reduce)').matches ? 'auto' : 'smooth',
          });
        },
        { signal: controller.signal }
      );
    });

    updateDots();
  }

  /**
   * Fetches the recommendations and cached the result for future use
   * @param {string} productId
   * @param {string | undefined} sectionId
   * @param {string | undefined} intent
   * @returns {Promise<{ success: true, data: string } | { success: false, status: number }>}
   */
  async #fetchCachedRecommendations(productId, sectionId, intent) {
    const url = `${this.dataset.url}&product_id=${productId}&section_id=${sectionId}&intent=${intent}`;

    const cachedResponse = this.#cachedRecommendations[url];
    if (cachedResponse) {
      return { success: true, data: cachedResponse };
    }

    this.#activeFetch?.abort();
    this.#activeFetch = new AbortController();

    try {
      const response = await fetch(url, { signal: this.#activeFetch.signal });
      if (!response.ok) {
        return { success: false, status: response.status };
      }

      const text = await response.text();
      this.#cachedRecommendations[url] = text;
      return { success: true, data: text };
    } finally {
      this.#activeFetch = null;
    }
  }

  /**
   * Handle errors in a consistent way
   * @param {Error} error
   */
  #handleError(error) {
    console.error('Product recommendations error:', error.message);
    this.classList.add('hidden');
    this.dataset.error = 'Error loading product recommendations';
  }
}

if (!customElements.get('product-recommendations')) {
  customElements.define('product-recommendations', ProductRecommendations);
}
